<script lang="ts" setup>
import { useAccount, useBalance } from '@wagmi/vue';

import { useUserStore } from '@/stores/user/useUserStore';
import { ScopeWagmiKeys } from '@/types';
import { useNuxtApp } from '#app';

type Props = {
    isMobile?: boolean;
};

const props = defineProps<Props>();

const {
    '$wagmi': {
        'wallet': {
            isOpenWallet,
            toggleOpenWallet,
            setVisibleOpenWallet
        }
    }
} = useNuxtApp();

const userStore = useUserStore();

const {
    isConnected,
    isConnecting,
    address,
    isReconnecting,
    chainId
} = useAccount();

const {
    'data': balance,
    isLoading
} = useBalance({
    address,
    chainId,
    scopeKey: ScopeWagmiKeys.USE_BALANCE
});

</script>

<template>
  <div class="wallet-container display-none-bp-576">
    <Transition mode="out-in"
                name="slide-fade"
    >
      <div v-if="isConnecting || isReconnecting || isLoading"
           key="spinner"
      >
        <LoaderSpinner :size="52">
          <IconMain :size="23"
                    icon="wallet"
          />
        </LoaderSpinner>
      </div>
      <Dropdown v-else
                key="content"
                :is-open="isOpenWallet"
                :position="props.isMobile ? 'headerFixed' : 'bottomLeft'"
                @update:is-open="setVisibleOpenWallet"
      >
        <template #toggle>
          <Transition mode="out-in"
                      name="slide-fade"
          >
            <button v-if="!balance || isConnecting"
                    key="wallet"
                    :class="{ 'btn-wallet--disabled': !balance && !!userStore.user }"
                    :disabled="!balance && !!userStore.user"
                    aria-label="Select wallet"
                    title="Choose wallet"
                    class="btn-wallet justify-center align-items-center"
                    type="button"
                    @click="toggleOpenWallet"
            >
              <span class="btn-wallet_layer1">
                <span class="btn-wallet_layer2 display-flex justify-center align-items-center">
                  <IconMain :size="23"
                            icon="wallet"
                  />
                </span>
              </span>
            </button>
            <div v-else-if="isConnected && balance"
                 key="balance"
                 :aria-label="isOpenWallet ? 'hide wallet' : 'show wallet'"
                 role="button"
                 type="button"
                 @click="toggleOpenWallet"
            >
              <Suspense>
                <template #fallback>
                  <LoaderSpinner :size="52">
                    <IconMain :size="23"
                              icon="wallet"
                    />
                  </LoaderSpinner>
                </template>
                <LazyWeb3ButtonBalance :balance="balance"
                                       :is-active="isOpenWallet"
                />
              </Suspense>
            </div>
          </Transition>
        </template>
        <template #content>
          <Transition name="slide-fade">
            <div v-if="isOpenWallet"
                 class="connect-wallet"
            >
              <Suspense>
                <template #fallback>
                  <div class="connect-wallet-suspense">
                    <LoaderDots />
                  </div>

                </template>
                <LazyWeb3Wallet />
              </Suspense>
            </div>
          </Transition>
        </template>
      </Dropdown>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>

.connect-wallet {
  position: relative;
  width: 348px;
  border-radius: var(--background-500-radius, 4px);
  background-color: var(--layer-floor-2);

  &-suspense {
    padding-top: 24px;
    padding-bottom: 24px;
  }

  @include mobile-576 {
    width: 100%;
    height: 100%;
  }
}

.slide-enter-active, .slide-leave-to {
  transform: scaleY(0);
}

.btn-wallet {
  display: inline-flex;
  position: relative;
  flex-shrink: 0;
  min-width: var(--control-500-size-single, 52px);
  height: var(--control-500-size-single, 52px);
  padding: 0;
  overflow: hidden;
  transition: opacity 0.3s ease;
  border: 2px solid transparent;
  border-radius: 50%;
  background-clip: padding-box, border-box;
  background-image: linear-gradient(to right, #93FD75, #8F75FE);
  cursor: pointer;
  background-origin: padding-box, border-box;

  &:hover {
    opacity: 0.8;
  }

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}

.btn-wallet_layer1 {
  display: block;
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  border: 2px solid transparent;
  border-radius: inherit;
  background-color: transparent;
}

.btn-wallet_layer2 {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background-color: var(--bg-secondary);
  color: var(--content-primary);
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.8s cubic-bezier(1, 0.8, 0.9, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(5px);
  opacity: 0;
}

</style>
